import { api } from "@shared/lib/api";
import { ACCOUNT_STATUS, BackednResponse } from "@shared/lib/types";

import {
  Agency,
  AgencyUser,
  Contact,
  CreateAgencyRequest,
  IGetAgencies,
  Review,
} from "@shared/lib/types/agency";

export class AgencyService {
  public static async getAgencys(
    searchQuery?: string,
    review?: number,
    status?: ACCOUNT_STATUS[],
    limit?: number,
    pageNo?: number
  ) {
    const params = new URLSearchParams();

    if (searchQuery) {
      params.append("searchQuery", searchQuery);
    }
    if (review) {
      params.append("review", review.toString());
    }
    if (limit) {
      params.append("limit", limit.toString());
    }
    if (pageNo) {
      params.append("pageNo", pageNo.toString());
    }
    if (status && status.length) {
      params.append("status", status.join("$$"));
    }

    // Convert params to string and add to URL only if there are parameters
    const queryString = params.toString();
    const url = `agency/${queryString ? `?${queryString}` : ""}`;
    const brands = (await api.get(url).json()) as BackednResponse<IGetAgencies>;

    return brands;
  }

  public static async createAgency(agency: CreateAgencyRequest) {
    const { name, profilePicUrl, companyName, type } = agency;
    await api.post("agency", {
      json: {
        name,
        profilePicUrl,
        companyName,
        type,
      },
    });
  }
  public static async getAgencyById(id: string) {
    const url = `agency/${id}`;
    const brand = (await api.get(url).json()) as BackednResponse<Agency>;
    return brand;
  }
  public static async addReview(
    agencyId: string,
    review: Pick<Review, "comment" | "review">
  ) {
    return (await api
      .post(`agency/review`, {
        json: {
          agencyId,
          ...review,
        },
      })
      .json()) as BackednResponse<Agency>;
  }
  public static async deleteReview(agencyId: string, reviewId: string) {
    return (await api
      .delete(`agency/review`, {
        json: {
          agencyId,
          reviewId,
        },
      })
      .json()) as BackednResponse<Agency>;
  }

  public static async updateAgency(agency: Partial<Agency>, agencyId: string) {
    return (await api
      .patch(`agency`, {
        json: {
          ...agency,
          agencyId,
        },
      })
      .json()) as BackednResponse<Agency>;
  }
  public static async updateNote(note: string, agencyId: string) {
    return (await api
      .post(`agency/note`, {
        json: {
          note,
          agencyId,
        },
      })
      .json()) as BackednResponse<Agency>;
  }
  public static async addContact(agencyId: string, contact: Contact) {
    return (await api
      .post(`user/agency`, {
        json: {
          agencyId,
          ...contact,
        },
      })
      .json()) as BackednResponse<Agency>;
  }
  public static async updateContact(agencyId: string, contact: Contact) {
    return (await api
      .patch(`user/agency`, {
        json: {
          agencyId,
          ...contact,
        },
      })
      .json()) as BackednResponse<Agency>;
  }

  public static async linkInfluencer(agencyId: string, influencerId: string) {
    return await api
      .post(`influencer/agency/link`, {
        json: { agencyId, influencerId },
      })
      .json();
  }
  public static async deLinkInfluencer(agencyId: string, influencerId: string) {
    return await api
      .delete(
        `influencer/agency/de-link?agencyId=${agencyId}&influencerId=${influencerId}`
      )
      .json();
  }

  public static async createAgencyUser(
    agencyId: string,
    agencyUser: AgencyUser
  ) {
    return await api
      .post(`user/agency`, {
        json: { agencyId, ...agencyUser },
      })
      .json();
  }

  public static async enableLoginAccess(agencyUserId: string) {
    return await api
      .put(`user/agency/login-access`, {
        json: { agencyUserId },
      })
      .json();
  }
  public static async editAgencyUser(agencyId: string, agencyUser: AgencyUser) {
    return await api
      .patch(`user/agency`, {
        json: { agencyId, ...agencyUser },
      })
      .json();
  }

  public static async editAgencyUserPermissions(
    agencyId: string,
    agencyUserId: string,
    permissions: string[]
  ) {
    return await api
      .patch(`user/agency`, {
        json: {
          agencyId,
          agencyUserId,
          permissions,
        },
      })
      .json();
  }

  public static async getAgencyPermissions() {
    const url = `permissions/agency`;
    const agencyPermissions = (await api.get(url).json()) as BackednResponse<
      string[]
    >;
    return agencyPermissions;
  }
}
